import { post, get } from "@/services/axiosUitils";
import {
  ASSESSMENT_API_CONFIG,
  getAssessmentEndpoints,
} from "../config/assessmentConfig";
import {
  type AssessmentPayload,
  type AssessmentAPIResponse,
} from "../types/assessmentTypes";
import {
  normalizeGender,
  normalizeRace,
  normalizeActivityLevel,
  normalizeWeightLossGoal,
  normalizeIntermittentFasting,
  normalizeFastingWindowType,
} from "../utils/assessmentNormalization";

// Re-export for backward compatibility
export { type AssessmentPayload } from "../types/assessmentTypes";

interface CachedSubmission {
  response: AssessmentAPIResponse;
  timestamp: number;
}

interface CachedTransform {
  payload: AssessmentPayload;
  timestamp: number;
}

/**
 * Assessment Service Class
 * Handles assessment submissions with caching and normalization
 */
class AssessmentService {
  // Cache configuration
  private submissionCache = new Map<string, CachedSubmission>();
  private transformCache = new Map<string, CachedTransform>();
  private readonly CACHE_EXPIRY_TIME = 10 * 60 * 1000; // 10 minutes
  private readonly MAX_CACHE_SIZE = 20;

  /**
   * Generate cache key for payload based on critical fields
   */
  private generatePayloadCacheKey(sessionData: any): string {
    const criticalFields = {
      gender: sessionData.gender,
      race: sessionData.race,
      date_of_birth: sessionData.date_of_birth,
      height_ft: sessionData.height_ft,
      height_inches: sessionData.height_inches,
      weight_kg: sessionData.weight_kg,
      diabetes_type: sessionData.diabetes_type,
    };
    return btoa(JSON.stringify(criticalFields));
  }

  /**
   * Generate cache key for submission
   */
  private generateSubmissionCacheKey(payload: AssessmentPayload): string {
    const payloadStr = JSON.stringify(payload);
    return btoa(payloadStr.slice(0, 100));
  }

  /**
   * Clean expired cache entries
   */
  private cleanExpiredCache(): void {
    const now = Date.now();

    for (const [key, cached] of this.submissionCache.entries()) {
      if (now - cached.timestamp > this.CACHE_EXPIRY_TIME) {
        this.submissionCache.delete(key);
      }
    }

    for (const [key, cached] of this.transformCache.entries()) {
      if (now - cached.timestamp > this.CACHE_EXPIRY_TIME) {
        this.transformCache.delete(key);
      }
    }
  }

  /**
   * Manage cache size (LRU eviction)
   */
  private manageCacheSize(): void {
    this.cleanExpiredCache();

    if (this.submissionCache.size > this.MAX_CACHE_SIZE) {
      const oldestKey = this.submissionCache.keys().next().value;
      if (oldestKey) {
        this.submissionCache.delete(oldestKey);
      }
    }

    if (this.transformCache.size > this.MAX_CACHE_SIZE) {
      const oldestKey = this.transformCache.keys().next().value;
      if (oldestKey) {
        this.transformCache.delete(oldestKey);
      }
    }
  }

  /**
   * Performance monitoring wrapper
   */
  private async measurePerformance<T>(
    _operation: string,
    fn: () => Promise<T>
  ): Promise<T> {
    return fn();
  }

  /**
   * Submit new assessment
   * Uses common post() utility - Token & Tenant-ID auto-injected!
   */
  submitNewAssessment = async (
    payload: AssessmentPayload
  ): Promise<AssessmentAPIResponse> => {
    // Check cache first (prevent duplicate submissions)
    const cacheKey = this.generateSubmissionCacheKey(payload);
    const cached = this.submissionCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_EXPIRY_TIME) {
      return {
        ...cached.response,
        message: "Assessment retrieved from cache (recent submission)",
      };
    }

    this.manageCacheSize();

    const endpoints = getAssessmentEndpoints();

    try {
      // ✅ Using common 'post' utility
      // Token, Tenant-ID automatically added by axiosConfig interceptor!
      const response = await this.measurePerformance(
        "Assessment Submission",
        () => post<any>(endpoints.newAssessment, payload)
      );

      const successResponse: AssessmentAPIResponse = {
        success: true,
        data: response,
        message: "Assessment submitted successfully",
      };

      // Cache successful submission
      this.submissionCache.set(cacheKey, {
        response: successResponse,
        timestamp: Date.now(),
      });

      return successResponse;
    } catch (error: any) {
      // Error already handled by axiosUitils handelError()
      throw new Error(error.message ?? "Assessment submission failed");
    }
  };

  /**
   * Test API connectivity
   */
  testAPIConnection = async (): Promise<boolean> => {
    try {
      const endpoints = getAssessmentEndpoints();

      // ✅ Using common 'get' utility
      await get(endpoints.testConnection);
      return true;
    } catch (_error) {
      return false;
    }
  };

  /**
   * Transform session data to API payload (with caching)
   */
  transformSessionDataToPayload = (sessionData: any): AssessmentPayload => {
    // Check cache first
    const cacheKey = this.generatePayloadCacheKey(sessionData);
    const cached = this.transformCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_EXPIRY_TIME) {
      return cached.payload;
    }

    // Transform to API format
    const payload: AssessmentPayload = {
      // Basic demographics - normalized
      gender: normalizeGender(sessionData.gender),
      race: normalizeRace(sessionData.race),
      date_of_birth: sessionData.date_of_birth,

      // Height - send only what user entered (either ft-inches or cm, not both)
      ...(sessionData.height_cm &&
      !sessionData.height_ft &&
      !sessionData.height_inches
        ? {
            height_in: "cm",
            height_cm: sessionData.height_cm,
          }
        : (sessionData.height_ft || sessionData.height_inches) &&
            !sessionData.height_cm
          ? {
              height_in: "ft_in",
              height_ft: sessionData.height_ft ?? 0,
              height_inches: sessionData.height_inches ?? 0,
            }
          : {
              height_in: ASSESSMENT_API_CONFIG.DEFAULTS.HEIGHT_UNIT,
              height_ft: ASSESSMENT_API_CONFIG.DEFAULTS.HEIGHT_FT,
              height_inches: ASSESSMENT_API_CONFIG.DEFAULTS.HEIGHT_INCHES,
            }),

      // Weight - send only what user entered (either kg or lbs, not both)
      ...(sessionData.weight_kg && !sessionData.weight_lbs
        ? {
            weight_in: "kg",
            weight_kg: sessionData.weight_kg,
          }
        : sessionData.weight_lbs && !sessionData.weight_kg
          ? {
              weight_in: "lbs",
              weight_lbs: sessionData.weight_lbs,
            }
          : {
              weight_in: ASSESSMENT_API_CONFIG.DEFAULTS.WEIGHT_UNIT,
              weight_kg: ASSESSMENT_API_CONFIG.DEFAULTS.WEIGHT_KG,
            }),

      // Target Weight - only include if user set a target (optional)
      ...(sessionData.target_weight_kg && !sessionData.target_weight_lbs
        ? {
            target_weight_in: "kg",
            target_weight_kg: sessionData.target_weight_kg,
          }
        : sessionData.target_weight_lbs && !sessionData.target_weight_kg
          ? {
              target_weight_in: "lbs",
              target_weight_lbs: sessionData.target_weight_lbs,
            }
          : {}),

      // Assessment fields
      diabetes_type:
        sessionData.diabetes_type ??
        ASSESSMENT_API_CONFIG.DEFAULTS.DIABETES_TYPE,
      weight_loss_goal: normalizeWeightLossGoal(sessionData.weight_loss_goal),
      // Only include weight_loss_reasons if user selected 'yes' for weight loss goal AND has actual reason
      ...(normalizeWeightLossGoal(sessionData.weight_loss_goal) === "yes" &&
      sessionData.weight_loss_reasons &&
      sessionData.weight_loss_reasons.trim() !== ""
        ? { weight_loss_reasons: sessionData.weight_loss_reasons }
        : {}),
      activity_level: normalizeActivityLevel(sessionData.activity_level),
      diet_adherence:
        sessionData.diet_adherence ??
        ASSESSMENT_API_CONFIG.DEFAULTS.DIET_ADHERENCE,
      intermittent_fasting: normalizeIntermittentFasting(
        sessionData.intermittent_fasting
      ),
      // Only include fasting_window_type if user follows intermittent fasting AND has selected a window type
      ...(normalizeIntermittentFasting(sessionData.intermittent_fasting) ===
        "Yes — I follow a fasting window" &&
      sessionData.fasting_window_type &&
      sessionData.fasting_window_type.trim() !== ""
        ? {
            fasting_window_type: normalizeFastingWindowType(
              sessionData.fasting_window_type
            ),
          }
        : {}),
      meal_times: sessionData.meal_times ?? [
        ...ASSESSMENT_API_CONFIG.DEFAULTS.MEAL_TIMES,
      ],
      medication_details: sessionData.medication_details ?? [
        ...ASSESSMENT_API_CONFIG.DEFAULTS.MEDICATION_DETAILS,
      ],
      insulin_details: sessionData.insulin_details ?? [
        ...ASSESSMENT_API_CONFIG.DEFAULTS.INSULIN_DETAILS,
      ],
    };

    // Cache the transformed payload
    this.manageCacheSize();
    this.transformCache.set(cacheKey, {
      payload,
      timestamp: Date.now(),
    });

    return payload;
  };

  /**
   * Create backend payload with defaults
   */
  createBackendPayload = (
    data?: Partial<AssessmentPayload>
  ): AssessmentPayload => {
    return {
      gender: data?.gender ?? ASSESSMENT_API_CONFIG.DEFAULTS.GENDER,
      race: normalizeRace(data?.race),
      date_of_birth: data?.date_of_birth,
      // Height - send only what user entered (either ft-inches or cm, not both)
      ...(data?.height_cm && !data?.height_ft && !data?.height_inches
        ? {
            height_in: "cm",
            height_cm: data?.height_cm,
          }
        : (data?.height_ft || data?.height_inches) && !data?.height_cm
          ? {
              height_in: "ft_in",
              height_ft: data?.height_ft ?? 0,
              height_inches: data?.height_inches ?? 0,
            }
          : {
              height_in: ASSESSMENT_API_CONFIG.DEFAULTS.HEIGHT_UNIT,
              height_ft: ASSESSMENT_API_CONFIG.DEFAULTS.HEIGHT_FT,
              height_inches: ASSESSMENT_API_CONFIG.DEFAULTS.HEIGHT_INCHES,
            }),
      // Weight - send only what user entered (either kg or lbs, not both)
      ...(data?.weight_kg && !data?.weight_lbs
        ? {
            weight_in: "kg",
            weight_kg: data?.weight_kg,
          }
        : data?.weight_lbs && !data?.weight_kg
          ? {
              weight_in: "lbs",
              weight_lbs: data?.weight_lbs,
            }
          : {
              weight_in: ASSESSMENT_API_CONFIG.DEFAULTS.WEIGHT_UNIT,
              weight_kg: ASSESSMENT_API_CONFIG.DEFAULTS.WEIGHT_KG,
            }),
      // Target Weight - only include if user set a target (optional)
      ...(data?.target_weight_kg && !data?.target_weight_lbs
        ? {
            target_weight_in: "kg",
            target_weight_kg: data?.target_weight_kg,
          }
        : data?.target_weight_lbs && !data?.target_weight_kg
          ? {
              target_weight_in: "lbs",
              target_weight_lbs: data?.target_weight_lbs,
            }
          : {}),
      diabetes_type:
        data?.diabetes_type ?? ASSESSMENT_API_CONFIG.DEFAULTS.DIABETES_TYPE,
      weight_loss_goal: normalizeWeightLossGoal(data?.weight_loss_goal),
      // Only include weight_loss_reasons if user selected 'yes' for weight loss goal AND has actual reason
      ...(normalizeWeightLossGoal(data?.weight_loss_goal) === "yes" &&
      data?.weight_loss_reasons &&
      data?.weight_loss_reasons.trim() !== ""
        ? { weight_loss_reasons: data?.weight_loss_reasons }
        : {}),
      activity_level: normalizeActivityLevel(data?.activity_level),
      diet_adherence:
        data?.diet_adherence ?? ASSESSMENT_API_CONFIG.DEFAULTS.DIET_ADHERENCE,
      intermittent_fasting: normalizeIntermittentFasting(
        data?.intermittent_fasting
      ),
      // Only include fasting_window_type if user follows intermittent fasting AND has selected a window type
      ...(normalizeIntermittentFasting(data?.intermittent_fasting) ===
        "Yes — I follow a fasting window" &&
      data?.fasting_window_type &&
      data?.fasting_window_type.trim() !== ""
        ? {
            fasting_window_type: normalizeFastingWindowType(
              data?.fasting_window_type
            ),
          }
        : {}),
      meal_times: data?.meal_times ?? [
        ...ASSESSMENT_API_CONFIG.DEFAULTS.MEAL_TIMES,
      ],
      medication_details: data?.medication_details ?? [
        ...ASSESSMENT_API_CONFIG.DEFAULTS.MEDICATION_DETAILS,
      ],
      insulin_details: data?.insulin_details ?? [
        ...ASSESSMENT_API_CONFIG.DEFAULTS.INSULIN_DETAILS,
      ],
    };
  };

  /**
   * Clear all caches
   */
  clearCache = (): void => {
    this.submissionCache.clear();
    this.transformCache.clear();
  };

  /**
   * Get cache statistics
   */
  getCacheStats = () => {
    return {
      submissionCache: {
        size: this.submissionCache.size,
        maxSize: this.MAX_CACHE_SIZE,
      },
      transformCache: {
        size: this.transformCache.size,
        maxSize: this.MAX_CACHE_SIZE,
      },
      expiryTime: this.CACHE_EXPIRY_TIME,
    };
  };

  /**
   * Force cache cleanup
   */
  forceCleanupCache = (): void => {
    this.cleanExpiredCache();
  };
}

// Export singleton instance (City Collab pattern)
const assessmentService = new AssessmentService();
export default assessmentService;
